<template>
  <PageBase class="scan-documents-view">
    <section class="scan-documents-view__content">
      <div v-if="isLoading" class="loading">
        <hd-loading-spinner class="loading__spinner" />
      </div>
      <template v-else>
        <template v-if="hasCamera && !hasScannedDocument">
          <h3 class="scan-documents-view__title">{{ $t('GENERAL.DOCUMENTS_SCANNER') }}</h3>

          <div class="scan-documents-view__select-deal">
            <p>{{ $t('SCAN_DOCUMENTS.SELECT_DEAL_LABEL') }}</p>
            <SearchSelect
              v-model="selectedDeal"
              :options="selectOptions"
              :filter-by="dealsSearchSelectFilter"
              :placeholder="$t('SCAN_DOCUMENTS.SELECT_DEAL_PLACEHOLDER')"
              class="scan-documents-view__select"
            />
          </div>

          <div class="scan-documents-view__cta">
            <router-link
              v-slot="{ navigate }"
              :to="{ name: documentsScannerRoute, query: { uid: selectedDealUid } }"
            >
              <HdButton :icon-src="cameraIcon" :disabled="!selectedDeal" @click="navigate">
                {{ $t('SCAN_DOCUMENTS.SCAN_BUTTON') }}
              </HdButton>
            </router-link>
          </div>

          <InfoBox
            :title="$t('SCAN_DOCUMENTS.EXISTING_DOCUMENT_SCAN_TITLE')"
            class="scan-documents-view__info-box"
          >
            <p>
              {{ $t('SCAN_DOCUMENTS.EXISTING_DOCUMENT_SCAN_TEXT_A') }}
              <HdIcon
                :src="documentImageIcon"
                :class-from-fill="{
                  '#8FC1FF': 'scan-documents-view__upload-file-icon--background',
                  '#0F1D2E': 'scan-documents-view__upload-file-icon--border',
                }"
                width="24"
                height="24"
                class="scan-documents-view__upload-file-icon"
              />
              {{ $t('SCAN_DOCUMENTS.EXISTING_DOCUMENT_SCAN_TEXT_B') }}
            </p>
          </InfoBox>
        </template>
        <div v-else-if="hasScannedDocument" class="scan-documents-view__success">
          <HdIcon :src="checkCircleIcon" />
          <h3>{{ $t('SCAN_DOCUMENTS.SUCCESS_TITLE') }}</h3>
          <p v-html="$t('SCAN_DOCUMENTS.SUCCESS_SUBTITLE', { selectedDealConnectDocumentsLink })" />
          <HdButton @click="startNewScan">
            {{ $t('SCAN_DOCUMENTS.ANOTHER_SCAN_BUTTON') }}
          </HdButton>
        </div>
        <div v-else-if="!hasCamera" class="scan-documents-view__error">
          <HdIcon :src="warningTriangleIcon" />
          <h3>{{ $t('SCAN_DOCUMENTS.NO_CAMERA_ERROR') }}</h3>
        </div>
      </template>
    </section>
  </PageBase>
</template>

<script>
import { omit as _omit } from 'lodash-es';
import { camera as cameraIcon } from 'homeday-assets';
import {
  checkCircle as checkCircleIcon,
  warningTriangle as warningTriangleIcon,
} from 'homeday-assets/L';
import { documentImage as documentImageIcon } from 'homeday-assets/M';
import { HdButton, HdIcon } from 'homeday-blocks';
import PageBase from '@/layout/PageBase.vue';
import InfoBox from '@/components/InfoBox.vue';
import SearchSelect from '@/components/SearchSelect.vue';
import HdLoadingSpinner from '@/components/HdLoadingSpinner.vue';
import { listAllDeals, uploadDocumentFiles } from '@/services/apiMapper';
import ROUTE_NAMES from '@/router/scanner/ROUTE_NAMES';

export default {
  name: 'ScanDocumentsView',
  components: {
    HdIcon,
    HdButton,
    InfoBox,
    PageBase,
    SearchSelect,
    HdLoadingSpinner,
  },
  data() {
    return {
      deals: [],
      selectedDeal: null,
      isLoading: true,
      hasCamera: false,
      hasScannedDocument: false,
      documentsScannerRoute: ROUTE_NAMES.DOCUMENTS_SCANNER,
      warningTriangleIcon,
      documentImageIcon,
      checkCircleIcon,
      cameraIcon,
    };
  },
  computed: {
    selectOptions() {
      return this.deals.map(
        ({
          uid,
          city,
          street,
          zip_code: zipCode,
          data_fields: { living_space: livingSpace, marketing_price: marketingPrice },
        }) => ({
          uid,
          livingSpace,
          marketingPrice,
          label: `${street}, ${zipCode} ${city}`,
        }),
      );
    },
    selectedDealUid() {
      return this.selectedDeal ? this.selectedDeal.uid : null;
    },
    selectedDealConnectDocumentsLink() {
      const connectBaseUrl = process.env.VUE_APP_HD_CONNECT;
      return this.selectedDeal
        ? `${connectBaseUrl}/crm/deals/${this.selectedDealUid}/documents`
        : null;
    },
  },
  created() {
    this.fetchDeals();
    if (['uid', 'name', 'content'].every((key) => key in this.$route.query)) {
      this.saveScannedDocument();
    } else {
      this.checkIfUserHasCamera();
    }
  },
  methods: {
    checkIfUserHasCamera() {
      if (!navigator.mediaDevices?.enumerateDevices) {
        this.hasCamera = false;
        this.isLoading = false;
        console.error('enumerateDevices() not supported.');
      } else {
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            this.hasCamera = devices.some((device) => device.kind === 'videoinput');
          })
          .catch((err) => {
            this.hasCamera = false;
            console.error(`${err.name}: ${err.message}`);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    async fetchDeals() {
      try {
        this.isLoading = true;
        this.deals = await listAllDeals();
        if (this.$route.query?.uid) {
          const preSelectedDeal = this.deals.find(({ uid }) => uid === this.$route.query.uid);
          this.selectedDeal = {
            ...preSelectedDeal,
            label: `${preSelectedDeal.street}, ${preSelectedDeal.zip_code} ${preSelectedDeal.city}`,
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    dealsSearchSelectFilter(option, _label, searchTerm) {
      return Object.values(option).some((field) => field?.includes(searchTerm));
    },
    async saveScannedDocument() {
      this.hasScannedDocument = true;
      const { uid: dealUid, name: fileName, content: base64Data } = this.$route.query;
      try {
        await uploadDocumentFiles({ dealUid, fileName, base64Data });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    startNewScan() {
      this.$router.replace({
        ...this.$router.currentRoute,
        query: _omit(this.$route.query, ['name', 'content']),
      });
      this.$router.go();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.scan-documents-view {
  &__content {
    padding: $sp-m + $sp-s $sp-s 0 $sp-s;

    @include for('tablet') {
      margin: 0 auto;
      max-width: 600px;
    }
  }

  &__title {
    margin-bottom: $sp-m + $sp-s;
  }

  &__select {
    margin-top: $sp-m;
  }

  &__cta {
    display: flex;
    justify-content: center;
    margin-top: $sp-l;
  }

  &__info-box {
    margin-top: $sp-l;

    svg {
      vertical-align: bottom;
    }
  }

  ::v-deep &__upload-file-icon {
    &--background {
      fill: transparent;
    }

    &--border {
      fill: $primary-color;
    }
  }

  &__success,
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    h3,
    p {
      margin-top: $sp-m;
    }

    button {
      margin-top: $sp-l;
    }
  }
}

.loading {
  display: flex;

  &__spinner {
    margin: auto;
  }
}
</style>
